import React, { FC, useState, useMemo, useCallback, ChangeEvent } from 'react';
import Lozenge from '@atlaskit/lozenge';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import InlineEdit from '@atlaskit/inline-edit';
import Textfield from '@atlaskit/textfield';
import EditIcon from '@atlaskit/icon/glyph/edit';
import Button from '@atlaskit/button';
import CopyIcon from '@atlaskit/icon/glyph/copy';

import { DAC_FAQS_DOC_LINK } from '@/constants';
import {
  extendWidth,
  flexSpace,
  flexStart,
  spaceGapCompact,
  spaceGapCozy,
  tooltipStyles,
} from '@/styles';
import { localNumberFormat } from '@/utils';
import { ExternalLinkAction, useActions } from '@/components/primitive';

import * as styles from '../summary-helpers/styles';

import { LowTrafficProps } from './types';
import {
  LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE,
  LOW_TRAFFIC_THRESHOLD_RANGE,
  thresholdTooLowMessage,
} from './constants';
import { getLowTrafficThreshold } from './utils';

const LowTrafficCalculator: FC<LowTrafficProps> = ({ objective }) => {
  const lowTrafficThresholdRecommended = useMemo(
    () => getLowTrafficThreshold(objective, LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE),
    [objective]
  );
  const [errorTolerance, setErrorTolerance] = useState<number>(LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE);
  const threshold: number = useMemo(() => getLowTrafficThreshold(objective, errorTolerance), [
    objective,
    errorTolerance,
  ]);
  const warningOverLimit: boolean = threshold > LOW_TRAFFIC_THRESHOLD_RANGE[1];
  const warningTooLow: boolean = threshold < lowTrafficThresholdRecommended;
  const hasWarning: boolean = warningOverLimit || warningTooLow;
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setErrorTolerance(Number(e.target.value.replace(/\D/g, '')));
  }, []);

  const { onCopy } = useActions(threshold.toString(), undefined, 'slo');

  return (
    <div css={[styles.equationStyles, flexStart, spaceGapCozy]}>
      <div css={styles.equationTitleStyles}>
        You can edit the 28 days <b>Error Tolerance</b> to calculate threshold:
      </div>
      <div css={[flexSpace, extendWidth]}>
        <div css={styles.equationRowStyles}>
          <div css={styles.equationColumnStyles}>
            <div css={styles.equationCellWithEditStyles}>
              <span css={[flexStart, spaceGapCompact]}>
                <b>Error Tolerance</b>
                <Tooltip
                  content={
                    <span css={tooltipStyles}>
                      Default 28 days <b>Error Tolerance</b> is{' '}
                      {LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE}. You can edit this value to calculate
                      the threshold.
                      <br />
                      <br />
                      <ExternalLinkAction
                        text="Find out more"
                        href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#how-do-i-calculate-the-low-traffic-threshold-for-my-slo-`}
                        hideIcon
                      />
                    </span>
                  }
                  position="right"
                >
                  <QuestionCircleIcon label="question-icon" size="small" />
                </Tooltip>
              </span>
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span>1</span>
              <span>-</span>
              <span>objective</span>
            </div>
          </div>
          <div>=</div>
          <div css={styles.equationColumnStyles}>
            <div css={[styles.equationCellWithEditStyles, styles.editStyles]}>
              <InlineEdit
                defaultValue={LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE}
                editView={fieldProps => (
                  <Textfield
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...fieldProps}
                    autoComplete="off"
                    autoFocus
                    isCompact
                    isMonospaced
                    isRequired
                    value={errorTolerance}
                    onChange={handleChange}
                    maxLength={LOW_TRAFFIC_THRESHOLD_RANGE[1].toString().length}
                  />
                )}
                readView={() => (
                  <span css={[flexStart, spaceGapCompact]}>
                    <span>{localNumberFormat(errorTolerance)}</span>
                    <EditIcon size="small" label="" />
                  </span>
                )}
                onConfirm={() => setErrorTolerance(errorTolerance)}
              />
            </div>
            <div css={styles.equationLineStyles} />
            <div css={styles.equationCellStyles}>
              <span css={styles.operatorStyles}>&#40;</span>
              <span>{100}</span>
              <span>-</span>
              <span>{objective}</span>
              <span css={styles.operatorStyles}>&#41;</span>
              <span>%</span>
            </div>
          </div>
          <div>=</div>
          <div>
            <span css={[flexStart, spaceGapCompact]}>
              <Lozenge
                isBold={!hasWarning}
                // eslint-disable-next-line no-nested-ternary
                appearance={hasWarning ? (warningTooLow ? 'moved' : 'removed') : 'new'}
              >
                {localNumberFormat(threshold)}
              </Lozenge>
              {hasWarning && (
                <Tooltip
                  content={
                    warningTooLow ? (
                      <>
                        {thresholdTooLowMessage}
                        <br />
                        <br />
                        You may want to increase the <b>Error Tolerance</b> rate to re-calculate the
                        threshold.
                      </>
                    ) : (
                      <>
                        The calculated value is more than the maximum threshold.
                        <br />
                        <br />
                        Please decrease the <b>Error Tolerance</b> rate to re-calculate the
                        threshold.
                      </>
                    )
                  }
                  position="right"
                >
                  <WarningIcon
                    size="small"
                    primaryColor={token(`color.text.accent.${warningTooLow ? 'orange' : 'red'}`)}
                    label="warning-icon"
                  />
                </Tooltip>
              )}
            </span>
          </div>
        </div>
        <div>
          <Button
            label="copy calculated value"
            iconBefore={<CopyIcon size="small" label="" />}
            onClick={onCopy}
          >
            Copy
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LowTrafficCalculator;
